import {
  ScheduleCompletionActions,
  ScheduleEntryState,
  Task,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';

import { computeHabitEntryUpdate } from './compute-habit-entry-update';
import { isNumber } from './is-number';
import { getActiveSchedule } from './schedules/get-active-schedule';

// For a caller that resolved the direction itself against a list that may since have gone stale.
export enum TaskCompletionDirection {
  Complete = 'complete',
  Uncomplete = 'uncomplete',
}

export type CompleteTaskForTodayOptions = {
  today: Timestamp;
  referenceDate?: Timestamp;
  type?: ScheduleEntryState;
  value?: number;
  increment?: number;
  toggleDayEntry?: boolean;
  // Absent means toggle, which is what every in-app call site wants.
  direction?: TaskCompletionDirection;
};

export enum CompleteTaskForTodayResultKind {
  Update = 'update',
  ManualEntryRequired = 'manual-entry-required',
  Ignored = 'ignored',
}

export type CompleteTaskForTodayResult =
  | { kind: CompleteTaskForTodayResultKind.Update; update: UpdateTask }
  | { kind: CompleteTaskForTodayResultKind.ManualEntryRequired }
  | { kind: CompleteTaskForTodayResultKind.Ignored };

export const completeTaskForToday = (
  task: Task,
  options: CompleteTaskForTodayOptions,
): CompleteTaskForTodayResult => {
  const {
    today,
    referenceDate,
    type = ScheduleEntryState.Complete,
    value,
    increment,
    toggleDayEntry,
    direction,
  } = options;

  if (task.type === TaskType.Task) {
    const becomingComplete = direction
      ? direction === TaskCompletionDirection.Complete
      : !task.completedAt;

    return {
      kind: CompleteTaskForTodayResultKind.Update,
      update: {
        id: task.id,
        completedAt: becomingComplete ? referenceDate ?? new Date() : null,
      },
    };
  }

  const activeSchedule = getActiveSchedule(task.schedules, today);
  if (
    activeSchedule?.action?.type ===
      ScheduleCompletionActions.LogCompletionValue &&
    !isNumber(value) &&
    !isNumber(increment)
  ) {
    return { kind: CompleteTaskForTodayResultKind.ManualEntryRequired };
  }

  const { newSchedules, ignored } = computeHabitEntryUpdate(
    task,
    referenceDate ?? new Date(),
    { today, type, value, increment, toggle: toggleDayEntry },
  );

  if (ignored) {
    return { kind: CompleteTaskForTodayResultKind.Ignored };
  }

  return {
    kind: CompleteTaskForTodayResultKind.Update,
    update: { id: task.id, schedules: newSchedules },
  };
};
